import { CognitoUser } from 'amazon-cognito-identity-js';
import axios from 'axios';
import { useQuery, useMutation } from 'react-query';
import { requestDelete, getBuildStageEndPoint, stripNonUUID } from '../utils/axiosFetch';
import { waitFor } from '../utils/helpers';

/** Internal fields not exposed by @aws-amplify typings */
interface CognitoUserInternals extends CognitoUser {
  // The authentication session generated when the user authenticates during login
  Session: string;
}

export interface UserPoolProxyAPIError {
  message: string;
  status: number;
}

const API_COGNITO_USERS = axios.create({
  baseURL: getBuildStageEndPoint('users'),
  headers: {
    'api-version': 'v1',
    Accept: 'application/json',
  },
});

API_COGNITO_USERS.interceptors.request.use(
  (config) => {
    if (config?.params?.token) {
      config.headers.Authorization = `Bearer ${config.params.token}`;
      delete config.params.token;
      return config;
    } else {
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

export function isUserPoolProxyAPIError(error: unknown): error is UserPoolProxyAPIError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

export function filterUserPoolProxyMessage(message: string): string {
  if (message.startsWith('You have made more than 5 attempts')) {
    return 'userPoolErrorMessages.tooManyWrongAttempts';
  } else if (message.startsWith('You input an incorrect recovery')) {
    return 'userPoolErrorMessages.incorrectResetCode';
  }
  return message;
}

/**
 * Gets the MFA Reset Code for the current Cognito user.
 *
 * @returns {Promise<string | null>}
 */
const getCognitoMFAResetCode = async (): Promise<{ code: string }> => {
  const idToken = sessionStorage['idToken'];
  if (!idToken) {
    throw new Error('Missing idToken');
  }

  try {
    const { data } = await API_COGNITO_USERS.get<{ code: string }>('/recoverycodes', {
      params: { token: idToken },
    });

    return data;
  } catch (error: any) {
    let errorMsg = error.response?.data?.message || error.message;
    console.error(errorMsg);
    return Promise.reject({ status: error.response?.status, message: errorMsg });
  }
};

export default {
  getCognitoMfaResetCode: () => useQuery('get-cognito-mfa-code', getCognitoMFAResetCode),
  useDeleteMFA: () => {
    return useMutation(
      async ({ cognitoUser, resetCode }: { cognitoUser?: CognitoUser; resetCode: string }) => {
        const cognitoId = cognitoUser?.getUsername();
        if (!cognitoId) throw new Error('Missing cognito user id');

        const resp = await requestDelete<null>(API_COGNITO_USERS, `/mfa/${cognitoId}`, {
          headers: {
            'Recovery-Code': stripNonUUID(resetCode),
            'Auth-Session': (cognitoUser as CognitoUserInternals).Session,
          },
        });
        // Wait 2s to avoid race condition. preferredMFA is wrong if user logins immediately after reset.
        await waitFor(2000);
        return resp;
      }
    );
  },
};
